import { ref } from 'lit/directives/ref.js';
import type { WebEditorElementDefinition as LandingEditorElementDefinition } from '../../element-definition.js';
import { renderEditorGeometryOverlay } from './editorGeometryOverlay.js';
import type { EditorFrame } from './editorPageConfig.js';
import { installEditorCanvasPanning, mountEditorPreviewViewport } from './editorPreviewViewport.js';
import { mountEditorTooltipRoot } from './editorTooltip.js';
import { selectPreviewElementBeforeProfileActivation } from '../../localization.js';
import type { EditorLoginProps } from '../EditorLogin.js';
type StaticPageKey = string;
export interface EditorShellAdapters {
  // pi-lens-ignore: no-unsafe-dictionary-any
  PropertiesView: new (context: Record<string, any>) => { renderers(): any };
  editorLogin: (props: EditorLoginProps) => unknown;
  renderEditorCommentComposer: (state: any) => unknown;
  renderEditorCommentPopover: (state: any) => unknown;
  renderEditorCommentsPanel: (state: any, page: any, audience: any, anchors: any, select: (comment: any) => void) => unknown;
  scrollEditorCommentIntoView: (id: string) => void;
  renderEditorAssetsPanel: (options: any) => unknown;
  renderEditorMediaInspector: () => unknown;
  renderEditorMediaLibrary: (inspect: () => void) => unknown;
}

export class WebEditorShell {
  // pi-lens-ignore: no-unsafe-dictionary-any
  constructor(private readonly context: Record<string, any>, private readonly adapters: EditorShellAdapters) {}

  render() {
    const { editorLogin, renderEditorCommentComposer, renderEditorCommentPopover, renderEditorCommentsPanel,
      scrollEditorCommentIntoView, renderEditorAssetsPanel, renderEditorMediaInspector, renderEditorMediaLibrary,
      PropertiesView } = this.adapters;
    const {
      activeEditorAudience,
      activeEditorPage,
      activeFrame,
      activePreviewProfile,
      activeTool,
      addSection,
      autoLayoutOperations,
      canDeleteDefinition,
      canDuplicateDefinition,
      changeZoom,
      childrenByParent,
      content,
      commentState,
      deleteDefinition,
      duplicateDefinition,
      editorPages,
      editorPreviewProfiles,
      editorRenderOptions,
      email,
      fieldValueForDefinition,
      platformAdminLoginHref,
      expandedSections,
      footerSection,
      future,
      geometryOperations,
      geometryState,
      handleLogin,
      handleLogout,
      handleEditorAudienceChange,
      handleMediaUpload,
      handlePreviewKeyDown,
      handlePreviewLinkClick,
      handlePreviewSectionDrop,
      handlePreviewTextCommit,
      handlePublish,
      handleRedo,
      handleSave,
      handleSectionDragOver,
      handleSectionDragStart,
      handleSectionDrop,
      handleSyncFromPublished,
      handleUndo,
      history,
      html,
      isAuthenticated,
      isCommentMode,
      isSelectedMedia,
      landingNavigation,
      landingPageContentRepository,
      landingProjectContentRepository,
      leftTab,
      leftPanelOpen,
      locale,
      localizationFlowActive,
      mediaAcceptForKind,
      mediaDefinitions,
      mediaLabelForKind,
      message,
      moveSection,
      orderedSections,
      orderedStaticPageBuiltInSections,
      password,
      project,
      propertiesTab,
      rightPanelOpen,
      renderLandingHomeSection,
      renderLocalizationFlow,
      renderMediaPreview,
      sectionTitle,
      selectedElementId,
      selectedDefinition,
      selectedFieldDefinition,
      selectedFrame,
      setActiveEditorAudience,
      setActiveFrame,
      setActivePreviewProfile,
      setActiveTool,
      setDraggedSection,
      setEmail,
      setLeftTab,
      setLeftPanelOpen,
      setLocale,
      setPassword,
      setPropertiesTab,
      setRightPanelOpen,
      setSelectedElementId,
      setShowSettings,
      showVersionHistory,
      staticPageContent,
      status,
      statusLabel,
      switchEditorPage,
      toggleSection,
      toggleVersionHistory,
      toggleCommentMode,
      visibleEditorSectionKeys,
      whiteLogo,
      zoom,
    } = this.context;
    const handlePreviewEditorKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'a' && event.shiftKey && selectedDefinition?.id !== 'page.root' && (selectedDefinition?.kind === 'container' || selectedDefinition?.kind === 'section')) {
        event.preventDefault();
        autoLayoutOperations.enableContainer(selectedElementId);
        return;
      }
      handlePreviewKeyDown(event);
    };
    const leafKindIcon = (kind: string) => {
      if (kind === 'text') return html`<span class="icon-[material-symbols--title] vp-layer-kind-icon"></span>`;
      if (kind === 'url') return html`<span class="icon-[material-symbols--link] vp-layer-kind-icon"></span>`;
      if (kind === 'image')
        return html`<span class="icon-[material-symbols--image-outline] vp-layer-kind-icon"></span>`;
      if (kind === 'video')
        return html`<span class="icon-[material-symbols--play-circle-outline] vp-layer-kind-icon"></span>`;
      return html`<span class="vp-layer-kind-icon"></span>`;
    };

    const layerActions = (options: {
      canDuplicate: boolean;
      canDelete: boolean;
      onDuplicate: () => void;
      onDelete: () => void;
    }) => html`
      <div class="vp-editor-layer-hover-actions">
        <button
          type="button"
          title="Duplizieren"
          aria-label="Duplizieren"
          ?disabled=${!options.canDuplicate}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            options.onDuplicate();
          }}
        >
          <span class="icon-[material-symbols--content-copy-outline]"></span>
        </button>
        <button
          type="button"
          title="Löschen"
          aria-label="Löschen"
          ?disabled=${!options.canDelete}
          @click=${(event: MouseEvent) => {
            event.stopPropagation();
            options.onDelete();
          }}
        >
          <span class="icon-[material-symbols--delete-outline]"></span>
        </button>
      </div>
    `;

    const renderLeafRow = (def: LandingEditorElementDefinition) => html`
      <div class="vp-layer-leaf vp-layer-entry ${selectedElementId === def.id ? 'is-active' : ''}">
        <button
          type="button"
          class="vp-layer-row"
          aria-current=${selectedElementId === def.id ? 'true' : 'false'}
          @click=${() => setSelectedElementId(def.id)}
        >
          ${leafKindIcon(def.kind)}
          <span class="vp-layer-name">${def.label}</span>
        </button>
        ${layerActions({
          canDuplicate: canDuplicateDefinition(def),
          canDelete: canDeleteDefinition(def),
          onDuplicate: () => duplicateDefinition(def),
          onDelete: () => deleteDefinition(def),
        })}
      </div>
    `;

    const layerIcon = (definition: LandingEditorElementDefinition) => {
      if (definition.kind === 'section') return 'icon-[material-symbols--view-day-outline]';
      if (definition.kind === 'container') return 'icon-[material-symbols--dashboard-outline]';
      if (definition.kind === 'image') return 'icon-[material-symbols--image-outline]';
      if (definition.kind === 'video') return 'icon-[material-symbols--play-circle-outline]';
      if (definition.kind === 'url') return 'icon-[material-symbols--link]';
      return 'icon-[material-symbols--title]';
    };

    const renderLayerNode = (definition: LandingEditorElementDefinition) => {
      const children = childrenByParent[definition.id] ?? [];
      if (!children.length && definition.kind !== 'section' && definition.kind !== 'container')
        return renderLeafRow(definition);

      const expanded = expandedSections[definition.id] ?? true;
      const section = definition.kind === 'section' ? definition.section : undefined;
      const canDragSection = Boolean(section);
      return html`
        <div class="vp-layer-section-group">
          <div class="vp-layer-section-head vp-layer-entry ${selectedElementId === definition.id ? 'is-active' : ''}">
            <button
              type="button"
              class="vp-layer-section-header"
              aria-current=${selectedElementId === definition.id ? 'true' : 'false'}
              draggable=${canDragSection ? 'true' : 'false'}
              @click=${() => {
                setSelectedElementId(definition.id);
                if (children.length) toggleSection(definition.id);
              }}
              @dragstart=${(event: DragEvent) => {
                if (section) handleSectionDragStart(event, section);
              }}
              @dragend=${() => setDraggedSection(null)}
              @dragover=${handleSectionDragOver}
              @drop=${(event: DragEvent) => {
                if (section) handleSectionDrop(event, section);
              }}
            >
              <span
                class="${children.length && expanded
                  ? 'icon-[material-symbols--keyboard-arrow-down]'
                  : 'icon-[material-symbols--keyboard-arrow-right]'} vp-layer-chevron"
              ></span>
              <span class="${layerIcon(definition)} vp-layer-section-icon"></span>
              <span class="vp-layer-section-title">${definition.label}</span>
            </button>
            ${layerActions({
              canDuplicate: canDuplicateDefinition(definition),
              canDelete:
                canDeleteDefinition(definition) &&
                (!section || activeEditorPage !== 'landing' || orderedSections.length > 1),
              onDuplicate: () => duplicateDefinition(definition),
              onDelete: () => deleteDefinition(definition),
            })}
          </div>
          ${expanded
            ? html`<div class="vp-layer-section-children">${children.map((child: LandingEditorElementDefinition) => renderLayerNode(child))}</div>`
            : ''}
        </div>
      `;
    };

    const renderLayerRow = (definition: LandingEditorElementDefinition, depth = 1) => html`
      <div
        class="vp-editor-layer-row vp-layer-entry ${selectedElementId === definition.id ? 'is-active' : ''}"
        style=${`--depth: ${depth};`}
      >
        <button
          type="button"
          class="vp-editor-layer-main"
          aria-current=${selectedElementId === definition.id ? 'true' : 'false'}
          @click=${() => setSelectedElementId(definition.id)}
        >
          <span class="vp-editor-layer-name">${definition.label}</span>
        </button>
        ${layerActions({
          canDuplicate: canDuplicateDefinition(definition),
          canDelete: canDeleteDefinition(definition),
          onDuplicate: () => duplicateDefinition(definition),
          onDelete: () => deleteDefinition(definition),
        })}
      </div>
    `;

    const renderLayersPanel = () => html`
      <div class="vp-layers-tree">
        ${(childrenByParent['page.root'] ?? []).map((definition: LandingEditorElementDefinition) => renderLayerNode(definition))}
      </div>
    `;

    const renderPreviewPage = (
      profile: { id: string; editorFrame: EditorFrame },
      inlineEditingElementId?: string,
    ) => {
      let profileActivated = activePreviewProfile === profile.id && activeFrame === profile.editorFrame;
      const activateProfile = () => {
        if (profileActivated) return;
        profileActivated = true;
        if (activePreviewProfile !== profile.id) setActivePreviewProfile(profile.id);
        if (activeFrame !== profile.editorFrame) setActiveFrame(profile.editorFrame);
      };
      const previewEditorRenderOptions = {
        ...editorRenderOptions,
        inlineEditingElementId,
        onElementSelect: (elementId: string) => {
          selectPreviewElementBeforeProfileActivation(
            elementId,
            editorRenderOptions.onElementSelect,
            () => activateProfile(),
          );
        },
        onElementPointerDown: editorRenderOptions.onElementPointerDown
          ? (event: PointerEvent, elementId: string) => {
              editorRenderOptions.onElementPointerDown?.(event, elementId);
              activateProfile();
            }
          : undefined,
      };
      return activeEditorPage === 'landing'
        ? html`
            <div class="vp-page vp-editor-preview-page">
              ${landingNavigation({
                content,
                currentLocale: locale,
                onLanguageChange: setLocale,
                audience: activeEditorAudience,
                onAudienceChange: handleEditorAudienceChange,
                editor: previewEditorRenderOptions,
              })}
              <main>
                ${orderedSections.map((section: string) => {
                  try {
                    return renderLandingHomeSection(section, content, previewEditorRenderOptions);
                  } catch (error) {
                    console.warn(`Failed to render section '${section}':`, error);
                    return html`<div class="vp-editor-section-error">Failed to render section: ${section}</div>`;
                  }
                })}
              </main>
              ${footerSection(content, previewEditorRenderOptions)}
            </div>
          `
        : staticPageContent({
            pageKey: activeEditorPage as StaticPageKey,
            content,
            page: landingProjectContentRepository.staticPage(
              project,
              locale,
              activeEditorAudience,
              activeEditorPage as StaticPageKey,
            ),
            currentLocale: locale,
            onLanguageChange: setLocale,
            audience: activeEditorAudience,
            onAudienceChange: handleEditorAudienceChange,
            editor: previewEditorRenderOptions,
            className: 'vp-page vp-editor-preview-page',
            sections: orderedSections,
            staticPageSectionOrder: orderedStaticPageBuiltInSections,
          });
    };

    const renderPreviewFrame = (profile: {
      id: string;
      label: string;
      width: number;
      height: number;
      editorFrame: EditorFrame;
    }) => {
      const isActive = activePreviewProfile === profile.id;
      const inlineEditingElementId = isActive ? editorRenderOptions.inlineEditingElementId : undefined;
      const previewRenderKey = [
        project.updatedAt,
        locale,
        activeEditorAudience,
        activeEditorPage,
        selectedElementId,
        activeTool,
        inlineEditingElementId ?? '',
        [...(editorRenderOptions.flippedFanSections ?? [])].sort().join(','),
      ].join('|');
      return html`
        <section class="vp-editor-frame-shell">
          <button
            type="button"
            class="vp-editor-frame-label ${isActive ? 'is-active' : ''}"
            aria-pressed=${String(isActive)}
            @click=${() => {
              setActivePreviewProfile(profile.id);
              setActiveFrame(profile.editorFrame);
            }}
          >
            ${profile.label} · ${profile.width} px
          </button>
          <iframe
            class="vp-editor-frame ${isActive ? 'is-active' : ''} ${profile.id === 'mobile' ? 'is-mobile' : ''}"
            data-profile-id=${profile.id}
            width=${String(profile.width)}
            height=${String(profile.height)}
            title=${`${profile.label}, ${profile.width} Pixel, bearbeitbar`}
            ${ref(element => {
              if (!(element instanceof HTMLIFrameElement)) return;
              mountEditorPreviewViewport(
                element,
                html`
                  <div
                    class="vp-editor-preview-event-root"
                    @focusout=${isCommentMode ? undefined : handlePreviewTextCommit}
                    @keydown=${isCommentMode ? undefined : handlePreviewEditorKeyDown}
                    @dragover=${isCommentMode ? undefined : handleSectionDragOver}
                    @drop=${isCommentMode ? undefined : handlePreviewSectionDrop}
                    @click=${(event: MouseEvent) => {
                      setActivePreviewProfile(profile.id);
                      setActiveFrame(profile.editorFrame);
                      if (!isCommentMode) handlePreviewLinkClick(event);
                    }}
                  >
                    ${renderPreviewPage(profile, inlineEditingElementId)}
                  </div>
                `,
                profile.height,
                previewRenderKey,
              );
            })}
          ></iframe>
        </section>
      `;
    };

    const renderPagesPanel = () => html`
      <section class="vp-editor-panel-section">
        ${editorPages.map(
          (page: { key: string; path: string; label: string }) => html`
            <button
              type="button"
              class="vp-editor-page-row ${activeEditorPage === page.key ? 'is-active' : ''}"
              @click=${() => switchEditorPage(page.key)}
            >
              <span>${page.label}</span>
              <small>${page.path}</small>
            </button>
          `,
        )}
        <div class="vp-editor-locale-row">
          ${landingPageContentRepository.locales.map(
            (option: string) => html`
              <button type="button" class=${locale === option ? 'is-active' : ''} @click=${() => setLocale(option)}>
                ${option.toUpperCase()}
              </button>
            `,
          )}
        </div>
        ${activeEditorPage !== 'landing'
          ? html`
              <div class="vp-editor-locale-row">
                <button
                  type="button"
                  class=${activeEditorAudience === 'applicant' ? 'is-active' : ''}
                  @click=${() => setActiveEditorAudience('applicant')}
                >
                  Bewerber
                </button>
                <button
                  type="button"
                  class=${activeEditorAudience === 'company' ? 'is-active' : ''}
                  @click=${() => setActiveEditorAudience('company')}
                >
                  Unternehmen
                </button>
              </div>
            `
          : ''}
      </section>

      <section class="vp-editor-panel-section">
        ${orderedSections.map(
          (section: string) => html`
            <div
              class="vp-editor-page-section-row ${selectedElementId === `section.${section}` ? 'is-active' : ''}"
              draggable="true"
              @dragstart=${(event: DragEvent) => handleSectionDragStart(event, section)}
              @dragend=${() => setDraggedSection(null)}
              @dragover=${handleSectionDragOver}
              @drop=${(event: DragEvent) => handleSectionDrop(event, section)}
            >
              <button type="button" @click=${() => setSelectedElementId(`section.${section}`)}>
                ${sectionTitle(section)}
              </button>
              <span>
                <button type="button" title="Section nach oben" aria-label="Section nach oben verschieben" @click=${() => moveSection(section, -1)}><span class="icon-[material-symbols--arrow-upward]"></span></button>
                <button type="button" title="Section nach unten" aria-label="Section nach unten verschieben" @click=${() => moveSection(section, 1)}><span class="icon-[material-symbols--arrow-downward]"></span></button>
              </span>
            </div>
          `,
        )}
      </section>
      ${(() => {
        const addableSections = visibleEditorSectionKeys.filter((section: string) => !orderedSections.includes(section));
        if (!addableSections.length) return '';
        return html`
          <section class="vp-editor-panel-section">
            <p class="vp-editor-kicker">Section hinzufügen</p>
            <div class="vp-editor-add-section-list">
              ${addableSections.map(
                (section: string) => html`
                  <button type="button" class="vp-editor-add-section" @click=${() => addSection(section)}>
                    <span class="icon-[material-symbols--add-circle-outline]"></span>
                    ${sectionTitle(section)}
                  </button>
                `,
              )}
            </div>
          </section>
        `;
      })()}
    `;

    const renderAssetsPanel = () => renderEditorAssetsPanel({
      sections: visibleEditorSectionKeys,
      orderedSections,
      mediaDefinitions,
      selectedFieldDefinition,
      isSelectedMedia,
      fieldValueForDefinition,
      addSection,
      setSelectedElementId,
      handleSectionDragStart,
      setDraggedSection,
      renderLayerRow,
      mediaLabelForKind,
      mediaAcceptForKind,
      handleMediaUpload,
      renderMediaPreview,
    });

    const renderLeftPanel = () => {
      if (leftTab === 'pages') return renderPagesPanel();
      if (leftTab === 'assets') return renderAssetsPanel();
      if (leftTab === 'media') return renderEditorMediaLibrary(() => setRightPanelOpen(true));
      return renderLayersPanel();
    };

    const {
      renderSectionToolbar,
      renderTestimonialToolbar,
      renderFloatingTextToolbar,
      renderInlineTextEditor,
      renderDesignProperties,
      renderPrototypeProperties,
      renderVersionHistoryPopover,
      renderSettingsModal,
    } = new PropertiesView(this.context).renderers();

    if (!isAuthenticated) {
      return editorLogin({
        email,
        password,
        message,
        isLoading: status === 'loading',
        handleLogin,
        setEmail,
        setPassword,
      });
    }

    return html`
      <div ${ref(mountEditorTooltipRoot)} class="vp-editor-app ${isCommentMode ? 'is-comment-mode' : ''} ${localizationFlowActive ? 'is-localization-flow' : ''} ${activeTool === 'hand' ? 'is-hand-tool' : ''} ${activeFrame === 'mobile' ? 'is-mobile-frame' : 'is-desktop-frame'} ${leftPanelOpen ? '' : 'is-left-collapsed'} ${rightPanelOpen ? '' : 'is-right-collapsed'}">
        ${isCommentMode ? html`${renderEditorCommentComposer(commentState)}${renderEditorCommentPopover(commentState)}` : localizationFlowActive ? '' : html`${renderSectionToolbar()} ${renderTestimonialToolbar()} ${renderFloatingTextToolbar()}`}
        ${isCommentMode || localizationFlowActive ? '' : renderEditorGeometryOverlay({
          definition: selectedDefinition,
          frame: selectedFrame,
          project,
          activeFrame,
          state: geometryState,
          operations: geometryOperations,
        })}
        ${isCommentMode ? '' : renderInlineTextEditor()}
        ${isCommentMode ? '' : renderVersionHistoryPopover()}
        <header class="vp-editor-toolbar">
          <div class="vp-editor-toolbar-brand">
            <img src=${whiteLogo} alt="Viva Porta" style="height: 20px;" />
            <button
              type="button"
              class=${`vp-editor-status-button ${showVersionHistory ? 'is-active' : ''}`}
              title="Versionsverlauf öffnen"
              ?disabled=${isCommentMode || localizationFlowActive}
              aria-expanded=${String(showVersionHistory)}
              @click=${toggleVersionHistory}
            >
              <span class="icon-[material-symbols--history] vp-editor-toolbar-icon"></span>
              ${statusLabel(status)}
            </button>
            <button type="button" class="vp-editor-icon-button" title="Rückgängig" data-editor-shortcut="⌘ Z" aria-label="Rückgängig" ?disabled=${isCommentMode || localizationFlowActive || history.length === 0} @click=${handleUndo}>
              <span class="icon-[material-symbols--undo] vp-editor-toolbar-icon"></span>
            </button>
            <button type="button" class="vp-editor-icon-button" title="Wiederholen" data-editor-shortcut="⇧ ⌘ Z" aria-label="Wiederholen" ?disabled=${isCommentMode || localizationFlowActive || future.length === 0} @click=${handleRedo}>
              <span class="icon-[material-symbols--redo] vp-editor-toolbar-icon"></span>
            </button>
          </div>
          <div class="vp-editor-toolbar-center">
            <div class="vp-editor-tool-segment">
              <button
                type="button"
                title="Auswählen"
                aria-label="Auswählen"
                class=${activeTool === 'select' ? 'is-active' : ''}
                ?disabled=${localizationFlowActive}
                @click=${() => setActiveTool('select')}
              >
                <span class="icon-[material-symbols--near-me-outline] vp-editor-toolbar-icon"></span>
              </button>
              <button
                type="button"
                title="Verschieben – mit mittlerer Maustaste ziehen"
                aria-label="Canvas verschieben (mittlere Maustaste)"
                class=${activeTool === 'hand' ? 'is-active' : ''}
                ?disabled=${localizationFlowActive}
                @click=${() => setActiveTool('hand')}
              >
                <span class="icon-[material-symbols--pan-tool-outline] vp-editor-toolbar-icon"></span>
              </button>
              <button type="button" title="Kommentieren" aria-label="Kommentiermodus umschalten" aria-pressed=${String(isCommentMode)} class=${isCommentMode ? 'is-active' : ''} ?disabled=${localizationFlowActive} @click=${toggleCommentMode}>
                <span class="icon-[material-symbols--chat-bubble-outline] vp-editor-toolbar-icon"></span>
              </button>
              <button type="button" title="Einstellungen" aria-label="Einstellungen öffnen" ?disabled=${isCommentMode || localizationFlowActive} @click=${() => setShowSettings(true)}>
                <span class="icon-[material-symbols--settings-outline] vp-editor-toolbar-icon"></span>
              </button>
              <button
                type="button"
                title="Alle Elemente sperren"
                class="vp-editor-icon-button"
                aria-label="Alle Elemente sperren"
                ?disabled=${isCommentMode || localizationFlowActive || geometryState.unlockedElementIds.size === 0}
                @click=${geometryState.lockAll}
              >
                <span class="icon-[material-symbols--lock-outline] vp-editor-toolbar-icon"></span>
              </button>
              <a
                class="vp-editor-icon-button"
                href=${platformAdminLoginHref()}
                target="_blank"
                rel="noopener noreferrer"
                title="Plattform-Admin-Login in neuem Tab öffnen"
                aria-label="Plattform-Admin-Login in neuem Tab öffnen"
                ><span class="icon-[material-symbols--database] vp-editor-toolbar-icon"></span
              ></a>
            </div>
          </div>
          <div class="vp-editor-toolbar-actions">
            <div class="vp-editor-zoom">
              <button type="button" class="vp-editor-icon-button" title="Verkleinern" aria-label="Ansicht verkleinern" @click=${() => changeZoom(-0.1)}><span class="icon-[material-symbols--remove]"></span></button>
              <span>${Math.round(zoom * 100)}%</span>
              <button type="button" class="vp-editor-icon-button" title="Vergrößern" aria-label="Ansicht vergrößern" @click=${() => changeZoom(0.1)}><span class="icon-[material-symbols--add]"></span></button>
            </div>
            <a class="vp-editor-icon-button" href="/" target="_blank" rel="noopener noreferrer" title="Live-Website öffnen" aria-label="Live-Website in neuem Tab öffnen"><span class="icon-[material-symbols--open-in-new]"></span></a>
            <button
              type="button"
              class="vp-editor-icon-button"
              title="Aktuellen Live-Stand aus der Datenbank in den Editor übernehmen"
              aria-label="Live-Stand in den Editor übernehmen"
              ?disabled=${isCommentMode || localizationFlowActive}
              @click=${handleSyncFromPublished}
            >
              <span class="icon-[material-symbols--sync] vp-editor-toolbar-icon"></span>
            </button>
            <button type="button" class="vp-editor-save-action" title="Änderungen speichern" data-editor-shortcut="⌘ S" ?disabled=${isCommentMode || localizationFlowActive} @click=${handleSave}>
              <span class="icon-[material-symbols--save-outline] vp-editor-toolbar-icon"></span> Speichern
            </button>
            <button type="button" class="is-primary" title="Website veröffentlichen" ?disabled=${isCommentMode || localizationFlowActive} @click=${handlePublish}><span class="icon-[material-symbols--publish]"></span> Publish</button>
            <button type="button" class="vp-editor-icon-button" title="Abmelden" aria-label="Vom Editor abmelden" @click=${handleLogout}><span class="icon-[material-symbols--logout]"></span></button>
          </div>
        </header>

        <aside class="vp-editor-sidebar" aria-hidden=${String(!leftPanelOpen || localizationFlowActive)} .inert=${!leftPanelOpen || localizationFlowActive}>
          ${isCommentMode ? html`<div class="vp-editor-comment-tab"><span class="icon-[material-symbols--folder-open-outline]"></span> Seiten & Ansichten</div>` : html`<div class="vp-editor-panel-tabs">
            <button type="button" title="Ebenen" aria-label="Ebenen" class=${leftTab === 'layers' ? 'is-active' : ''} @click=${() => setLeftTab('layers')}>
              <span class="icon-[material-symbols--layers-outline]"></span>
            </button>
            <button type="button" title="Seiten" aria-label="Seiten" class=${leftTab === 'pages' ? 'is-active' : ''} @click=${() => setLeftTab('pages')}>
              <span class="icon-[material-symbols--description-outline]"></span>
            </button>
            <button type="button" title="Assets" aria-label="Assets" class=${leftTab === 'assets' ? 'is-active' : ''} @click=${() => setLeftTab('assets')}>
              <span class="icon-[material-symbols--widgets]"></span>
            </button>
            <button type="button" title="Medien" aria-label="Medien" class=${leftTab === 'media' ? 'is-active' : ''} @click=${() => setLeftTab('media')}>
              <span class="icon-[material-symbols--perm-media]"></span>
            </button>
          </div>`}
          <div class="vp-editor-panel-scroll">${isCommentMode ? renderPagesPanel() : renderLeftPanel()}</div>
        </aside>
        <button
          type="button"
          class="vp-editor-panel-toggle is-left"
          aria-label=${leftPanelOpen ? 'Linke Seitenleiste einklappen' : 'Linke Seitenleiste ausklappen'}
          aria-expanded=${String(leftPanelOpen)}
          title=${leftPanelOpen ? 'Linke Seitenleiste einklappen' : 'Linke Seitenleiste ausklappen'}
          @click=${() => setLeftPanelOpen(!leftPanelOpen)}
        ><span class=${leftPanelOpen ? 'icon-[material-symbols--chevron-left]' : 'icon-[material-symbols--chevron-right]'}></span></button>

        <main class="vp-editor-canvas">
          ${renderLocalizationFlow()}
          <div
            class="vp-editor-canvas-scroll"
            ${ref(element => {
              if (element instanceof HTMLElement) installEditorCanvasPanning(element);
            })}
            @pointerdown=${(event: PointerEvent) => {
              if (activeTool === 'hand' || isCommentMode || localizationFlowActive) return;
              const target = event.target as HTMLElement | null;
              if (target?.closest('.vp-editor-frame-shell, .vp-editor-localization-prompt, .vp-editor-localization-stepper')) return;
              setSelectedElementId('page.root');
            }}
          >
            <div class="vp-editor-canvas-huge">
              <div class="vp-editor-canvas-wrapper" style=${`transform: scale(${zoom});`}>
                ${editorPreviewProfiles.map(renderPreviewFrame)}
              </div>
            </div>
          </div>
        </main>

        <aside class="vp-editor-properties" aria-hidden=${String(!rightPanelOpen || localizationFlowActive)} .inert=${!rightPanelOpen || localizationFlowActive}>
          ${isCommentMode
            ? html`<div class="vp-editor-comment-tab"><span class="icon-[material-symbols--checklist]"></span> Änderungswünsche</div>`
            : leftTab === 'media'
              ? html`<div class="vp-editor-comment-tab"><span class="icon-[material-symbols--perm-media]"></span> Medien-Details</div>`
              : html`<div class="vp-editor-panel-tabs">
            <button
              type="button"
              title="Design"
              aria-label="Design-Eigenschaften"
              class=${propertiesTab === 'design' ? 'is-active' : ''}
              @click=${() => setPropertiesTab('design')}
            >
              <span class="icon-[material-symbols--design-services-outline]"></span>
            </button>
            <button
              type="button"
              title="Prototype"
              aria-label="Prototype-Eigenschaften"
              class=${propertiesTab === 'prototype' ? 'is-active' : ''}
              @click=${() => setPropertiesTab('prototype')}
            >
              <span class="icon-[material-symbols--route-outline]"></span>
            </button>
          </div>`}
          <div class="vp-editor-panel-scroll">
            ${isCommentMode
              ? renderEditorCommentsPanel(commentState, activeEditorPage, activeEditorAudience, project.editorAnchorIds, comment => {
                  setActiveEditorAudience(comment.anchor.audience);
                  switchEditorPage(comment.anchor.pageKey);
                  window.setTimeout(() => scrollEditorCommentIntoView(comment.id), 120);
                })
              : leftTab === 'media'
                ? renderEditorMediaInspector()
                : propertiesTab === 'design'
                  ? renderDesignProperties()
                  : renderPrototypeProperties()}
            ${message ? html`<p class="vp-editor-message">${message}</p>` : ''}
          </div>
        </aside>
        <button
          type="button"
          class="vp-editor-panel-toggle is-right"
          aria-label=${rightPanelOpen ? 'Rechte Seitenleiste einklappen' : 'Rechte Seitenleiste ausklappen'}
          aria-expanded=${String(rightPanelOpen)}
          title=${rightPanelOpen ? 'Rechte Seitenleiste einklappen' : 'Rechte Seitenleiste ausklappen'}
          @click=${() => setRightPanelOpen(!rightPanelOpen)}
        ><span class=${rightPanelOpen ? 'icon-[material-symbols--chevron-right]' : 'icon-[material-symbols--chevron-left]'}></span></button>
        ${renderSettingsModal()}
      </div>
    `;
  }
}
